import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import "./ReleaseTimeline.css";

/**
 * Format an ISO date string into a human-readable date.
 */
function formatDate(isoString) {
    if (!isoString) return "—";
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

/**
 * Format an ISO date string into a relative "time ago" label.
 */
function timeAgo(isoString) {
    if (!isoString) return "";
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "";
    const diff = Date.now() - d.getTime();
    if (diff < 0) {
        const futureMins = Math.ceil(Math.abs(diff) / 60000);
        if (futureMins < 1) return "In a moment";
        if (futureMins < 60) return futureMins === 1 ? "In 1 minute" : `In ${futureMins} minutes`;
        const futureHrs = Math.floor(futureMins / 60);
        if (futureHrs < 24) return futureHrs === 1 ? "In 1 hour" : `In ${futureHrs} hours`;
        const futureDays = Math.floor(futureHrs / 24);
        if (futureDays < 30) return futureDays === 1 ? "In 1 day" : `In ${futureDays} days`;
        const futureMonths = Math.floor(futureDays / 30);
        if (futureMonths < 12) return futureMonths === 1 ? "In 1 month" : `In ${futureMonths} months`;
        const futureYears = Math.floor(futureMonths / 12);
        return futureYears === 1 ? "In 1 year" : `In ${futureYears} years`;
    }
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
}

/**
 * Shorten a release ID for display: first 8 chars + ellipsis.
 */
function shortReleaseId(releaseId) {
    if (!releaseId) return "—";
    if (releaseId.length <= 12) return releaseId;
    return releaseId.substring(0, 8) + "…";
}

/**
 * Turn FEATURE_DARK_MODE into "Dark Mode"
 */
function featureIdToLabel(featureId) {
    if (!featureId) return "Unknown Feature";
    return featureId
        .replace(/^FEATURE_/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * ReleaseTimeline
 * Displays the feature timeline for a client (API 7 response).
 * Premium vertical timeline with feature cards.
 *
 * @param {object[]} data        — from fetchClientFeatures()
 * @param {string}   title
 * @param {string}   description
 */
const ReleaseTimeline = ({ data = [], title, description }) => {
    const [searchTerm, setSearchTerm] = useState("");

    // Sort by release date (newest first); safe coercion and tie-breaker for stable order
    const sorted = useMemo(() => {
        const toTs = (item) => {
            const t = item?.releaseDate ? new Date(item.releaseDate).getTime() : NaN;
            return Number.isFinite(t) ? t : 0;
        };
        const items = [...data].sort((a, b) => {
            const tsA = toTs(a);
            const tsB = toTs(b);
            if (tsA !== tsB) return tsB - tsA;
            return String(a.releaseId || a.featureId || "").localeCompare(
                String(b.releaseId || b.featureId || ""),
                undefined,
                { numeric: true }
            );
        });
        if (!searchTerm.trim()) return items;
        const q = searchTerm.toLowerCase();
        return items.filter(
            (item) =>
                (item.featureId || "").toLowerCase().includes(q) ||
                (item.releaseId || "").toLowerCase().includes(q) ||
                featureIdToLabel(item.featureId).toLowerCase().includes(q)
        );
    }, [data, searchTerm]);

    return (
        <div className="rtl-card">
            {/* ── Header ──────────────────────────────────────────── */}
            <div className="rtl-card__header">
                <div className="rtl-card__header-left">
                    <h3 className="rtl-card__title">
                        {title || "Release Timeline"}
                    </h3>
                    <p className="rtl-card__desc">
                        {description || "Features released to this client"}
                    </p>
                </div>
                {data.length > 0 && (
                    <span className="rtl-card__count">
                        {data.length} feature{data.length !== 1 ? "s" : ""}
                    </span>
                )}
            </div>

            {/* ── Search (only when there's data) ─────────────────── */}
            {data.length > 0 && (
                <div className="rtl-search-bar">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        className="rtl-search-bar__input"
                        placeholder="Search features or release ID…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search features or release ID"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="rtl-search-bar__clear"
                            onClick={() => setSearchTerm("")}
                            aria-label="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>
            )}

            {/* ── Content ─────────────────────────────────────────── */}
            <div className="rtl-card__content">
                {data.length === 0 ? (
                    /* Empty state */
                    <div className="rtl-empty">
                        <div className="rtl-empty__icon" aria-hidden="true">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="1.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </div>
                        <p className="rtl-empty__title">No features released yet</p>
                        <p className="rtl-empty__sub">
                            This client has not received any features in this release.
                        </p>
                    </div>
                ) : sorted.length === 0 ? (
                    /* Search yields nothing */
                    <div className="rtl-empty">
                        <p className="rtl-empty__title">No matching features</p>
                        <p className="rtl-empty__sub">
                            Try a different search term.
                        </p>
                    </div>
                ) : (
                    /* Timeline */
                    <div className="rtl-timeline">
                        <div className="rtl-timeline__line" aria-hidden="true" />
                        {sorted.map((item, index) => (
                            <motion.div
                                key={`${item.featureId}-${item.releaseId}-${index}`}
                                className="rtl-timeline__entry"
                                initial={{ opacity: 0, x: -16 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.35, delay: index * 0.06 }}
                            >
                                {/* Dot */}
                                <div className="rtl-timeline__dot-wrap">
                                    <div className="rtl-timeline__dot">
                                        <div className="rtl-timeline__dot-inner" />
                                    </div>
                                </div>

                                {/* Card */}
                                <div className="rtl-timeline__card">
                                    <div className="rtl-timeline__card-top">
                                        <h4 className="rtl-timeline__feature-name">
                                            {featureIdToLabel(item.featureId)}
                                        </h4>
                                        <span className="rtl-timeline__status-badge">
                                            Released
                                        </span>
                                    </div>

                                    <div className="rtl-timeline__card-meta">
                                        <div className="rtl-timeline__meta-item">
                                            <span className="rtl-timeline__meta-label">Feature ID</span>
                                            <code className="rtl-timeline__meta-code">{item.featureId}</code>
                                        </div>
                                        <div className="rtl-timeline__meta-item">
                                            <span className="rtl-timeline__meta-label">Release</span>
                                            <code className="rtl-timeline__meta-code" title={item.releaseId}>
                                                {shortReleaseId(item.releaseId)}
                                            </code>
                                        </div>
                                        <div className="rtl-timeline__meta-item">
                                            <span className="rtl-timeline__meta-label">Released On</span>
                                            <span className="rtl-timeline__meta-value">
                                                {formatDate(item.releaseDate)}
                                            </span>
                                        </div>
                                        <div className="rtl-timeline__meta-item">
                                            <span className="rtl-timeline__meta-label">&nbsp;</span>
                                            <span className="rtl-timeline__meta-ago">
                                                {timeAgo(item.releaseDate)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReleaseTimeline;
